import React from 'react';
import { motion } from 'framer-motion';
import BlueHeaderSection from '../BlueHeader';

// Asset Imports
import webImg1 from "../../asset/images/web1.png";
import webImg2 from "../../asset/images/web.png";
import webImg3 from "../../asset/images/web3.png";
import webImg4 from "../../asset/images/web4.png";

// Consistent Animation Variant
const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.4, ease: 'easeOut' } 
  }
};

const services = [
  {
    id: "web-srv-1",
    title: 'Custom Website Development',
    text: 'Tailor-made websites built on modern frameworks like React, Angular, or Laravel — designed for speed, SEO, and scalability.',
  },
  {
    id: "web-srv-2",
    title: 'E-commerce Development',
    text: 'Conversion-driven stores on Shopify, WooCommerce, or custom stacks — with secure payments and inventory management.',
  },
  {
    id: "web-srv-3",
    title: 'CMS Development',
    text: 'WordPress, Strapi, or headless CMS solutions that empower your marketing and simplify content updates.',
  },
  {
    id: "web-srv-4",
    title: 'Web App Development',
    text: 'PWAs, dashboards, and portals with clean architecture, solid logic, and user-centric design.',
  },
  {
    id: "web-srv-5",
    title: 'Frontend & Backend Development',
    text: 'Pixel-perfect UIs + powerful backend using Node, Django, PHP, or .NET. API-integrated and future-proof.',
  },
  {
    id: "web-srv-6",
    title: 'Website Redesign & Optimization',
    text: 'Modern UX, faster load times, mobile-first, and SEO-enhanced for better performance and conversions.',
  }
];

const features = [
  {
    id: "web-feat-1",
    title: "Full-Stack Expertise",
    description: "End-to-end capabilities across frontend, backend, databases, and integrations so you work with one accountable team.",
    image: webImg1
  },
  {
    id: "web-feat-2",
    title: "Mobile-First & Responsive",
    description: "We design for mobile, tablet, and desktop from day one to ensure fast, consistent experiences across devices.",
    image: webImg2
  },
  {
    id: "web-feat-3",
    title: "Agile Methodology",
    description: "Short iterations, continuous feedback, and transparent communication keep your project on track and adaptable.",
    image: webImg3
  },
  {
    id: "web-feat-4",
    title: "Security & Compliance",
    description: "We follow secure coding, encryption, and best practices aligned with your industry's standards and regulations.",
    image: webImg4
  },
];

const WebDevelopment = () => {
  return (
    <div className="overflow-hidden">
      <BlueHeaderSection heading="WEB DEVELOPMENT" url="Web Development" showServices={true} />

      {/* Main Content Section */}
      <section className="py-12 md:py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">

          {/* Main Heading */}
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Building Fast, Secure, and Scalable Web Experiences
            </h2>
            <div className="w-24 h-1 bg-[#f7941d] mx-auto"></div>
          </div>

          {/* Intro Text */}
          <div className="mb-16 text-center">
            <p className="text-lg text-gray-600 leading-relaxed max-w-4xl mx-auto">
              At **BytesFarms Technologies**, we build modern, high-performing web apps tailored to your business goals, delivering user experiences that scale with your growth.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {services.map((item) => (
              <motion.div
                key={item.id} // Fixed: Using Unique ID instead of index
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className="p-6 rounded-2xl bg-white shadow-md border-l-4 border-[#f7941d] hover:shadow-lg transition-all duration-300"
              >
                <h4 className="text-xl font-semibold text-[#f7941d] mb-3">{item.title}</h4>
                <p className="text-gray-800 leading-relaxed tracking-tight">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-12 md:py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Why choose BytesFarms for Web Development?
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12">
            {features.map((feature) => (
              <motion.div
                key={feature.id} // Fixed: Using Unique ID instead of index
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                className="group relative overflow-hidden rounded-2xl shadow-2xl h-[220px] md:h-[250px]"
              >
                <img
                  src={feature.image}
                  alt={feature.title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/60 group-hover:bg-black/75 transition-opacity duration-300"></div>
                
                <div className="relative z-10 p-6 md:p-8 h-full flex flex-col justify-center text-center">
                  <h3 className="text-xl md:text-2xl font-bold text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-sm md:text-base text-gray-200 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebDevelopment;